/**
 * Instagram and Facebook media limits, in ONE place.
 *
 * Readiness (`posting-readiness.ts`, which also runs in Deno edge functions),
 * `src/constants/platform-specs.ts` and `src/utils/mediaValidation.ts` all read
 * these numbers, so the same rule can no longer be stated three different ways
 * (it was: Reels 15 to 90 s here, 3 to 180 s there, Stories 15 s in a third
 * file). `src/lib/platform-matrix/__tests__/meta-limits.guard.test.ts` fails if
 * any of the three drifts from this file.
 *
 * Plain constants only: no DOM, no Node, no `@/` alias, so Deno can import it.
 *
 * Sources, read 2026-11-03:
 *  - Instagram: https://developers.facebook.com/docs/instagram-platform/instagram-graph-api/reference/ig-user/media
 *    and https://developers.facebook.com/docs/instagram-platform/content-publishing
 *  - Facebook Reels: https://developers.facebook.com/docs/video-api/guides/reels-publishing
 *  - Facebook Pages photos: https://developers.facebook.com/docs/graph-api/reference/page/photos/
 *  - Facebook Page stories: https://developers.facebook.com/docs/page-stories-api
 */

export interface RatioWindow {
  /** Width divided by height. */
  min: number;
  max: number;
}

export const INSTAGRAM_LIMITS = {
  caption: { maxChars: 2200, maxHashtags: 30, maxMentions: 20 },
  /** Up to 20 images or videos in one carousel (Meta raised it from 10). */
  carouselMaxItems: 20,
  carouselMinItems: 2,
  /** JPEG only, 8 MB, 4:5 to 1.91:1, 320 to 1440 px wide. */
  image: {
    maxMB: 8,
    mimeTypes: ['image/jpeg'] as readonly string[],
    ratio: { min: 0.8, max: 1.91 } as RatioWindow,
    minWidthPx: 320,
    maxWidthPx: 1440,
  },
  altTextMaxChars: 1000,
  collaboratorsMax: 3,
  reel: {
    minSeconds: 3,
    maxSeconds: 900,
    maxMB: 300,
    /** Accepted window; 9:16 is only recommended. */
    ratio: { min: 0.01, max: 10 } as RatioWindow,
  },
  story: {
    minSeconds: 3,
    maxSeconds: 60,
    maxMB: 100,
    ratio: { min: 0.1, max: 10 } as RatioWindow,
  },
} as const;

export const FACEBOOK_LIMITS = {
  caption: { maxChars: 63206 },
  /** jpeg, bmp, png, gif, tiff; no WebP listed. 10 MB. */
  photo: {
    maxMB: 10,
    mimeTypes: [
      'image/jpeg',
      'image/png',
      'image/bmp',
      'image/gif',
      'image/tiff',
    ] as readonly string[],
  },
  /** Mora's own guard: Meta states no cap on `attached_media`. */
  albumMaxPhotos: 10,
  altTextMaxChars: 1000,
  reel: { minSeconds: 3, maxSeconds: 90, minWidthPx: 540, minHeightPx: 960 },
  /** Page stories cannot exceed 60 s even though a video is 3 to 90 s (page-stories-api). */
  story: { minSeconds: 3, maxSeconds: 60 },
} as const;

/** 9:16, the ratio both platforms recommend for vertical video. A mismatch is a warning, never a block. */
export const VERTICAL_VIDEO_RATIO = { width: 9, height: 16, tolerance: 0.05 } as const;

export type InstagramVideoPlacement = 'reel' | 'story';

export function instagramVideoLimits(placement: InstagramVideoPlacement) {
  return placement === 'story' ? INSTAGRAM_LIMITS.story : INSTAGRAM_LIMITS.reel;
}

/** True when `ratio` (width / height) is inside the window, ends included. */
export function ratioInWindow(ratio: number, window: RatioWindow): boolean {
  return ratio >= window.min && ratio <= window.max;
}

/** True when `ratio` is within the recommended 9:16 tolerance. */
export function isNearVerticalRatio(ratio: number): boolean {
  const expected = VERTICAL_VIDEO_RATIO.width / VERTICAL_VIDEO_RATIO.height;
  return Math.abs(ratio - expected) / expected <= VERTICAL_VIDEO_RATIO.tolerance;
}
